import * as z from 'zod/mini';

/**
 * The schema and the parse, with no access to `import.meta.env`.
 *
 * Split out from `env.ts` so the Vite config can import it and validate at **build** time. Runtime
 * validation alone is not enough: `VITE_*` values are inlined during the build, so a misconfigured
 * build would succeed, reach the CDN, and only fail once it was open on a player's phone. The
 * schema has to be readable from both a browser module and a Node build script, which means it
 * cannot be in the file that reads `import.meta.env`.
 */

const APP_ENVS = ['local', 'development', 'staging', 'production'] as const;

export type AppEnv = (typeof APP_ENVS)[number];

/**
 * Every message says what a correct value looks like. "Invalid input" is what Zod says by default,
 * and it sends the reader to the source to find out what the rule was.
 */

/** Accepts `"true"`/`"false"`; anything else is a configuration mistake, not a falsy value. */
const booleanish = z.pipe(
  z.enum(['true', 'false'], { error: 'must be exactly "true" or "false"' }),
  z.transform((v) => v === 'true'),
);

const positiveIntString = z.pipe(
  z
    .string({ error: 'must be a whole number of milliseconds' })
    .check(z.regex(/^\d+$/, 'must be a whole number of milliseconds')),
  z.transform(Number),
);

const schema = z.object({
  VITE_APP_NAME: z
    .string({ error: 'is required — the fallback product name shown before branding resolves' })
    .check(z.minLength(1, 'must not be empty')),
  VITE_API_BASE_URL: z
    .string({ error: 'is required — for example https://api.valuepay.example' })
    .check(
      z.refine((v) => /^https?:\/\/[^\s/]+/.test(v), {
        message: 'must be an absolute http(s) origin, for example https://api.valuepay.example',
      }),
      // A trailing slash produces `//v1/cashier/...`, which some gateways 404 and others redirect —
      // and a redirect on a POST loses the body.
      z.refine((v) => !v.endsWith('/'), { message: 'must not end with a trailing slash' }),
    ),
  VITE_APP_ENV: z.enum(APP_ENVS, {
    error: `must be one of: ${APP_ENVS.join(', ')}`,
  }),
  VITE_SUPPORT_URL: z.optional(z.string()),
  // Optional extra origin listed in CSP `img-src`. Not required for logos to render: the backend
  // returns absolute HTTPS URLs and `img-src` allows `https:` (ADR-036).
  VITE_ASSET_BASE_URL: z.optional(z.string()),
  VITE_POLL_BASE_MS: z._default(positiveIntString, 4000),
  VITE_POLL_MAX_MS: z._default(positiveIntString, 20_000),
  VITE_SESSION_WARN_SECONDS: z._default(positiveIntString, 120),
  VITE_ENABLE_DEV_TOOLS: z._default(booleanish, true),
  // Space-separated origins allowed to embed this page (merchant iframe). Empty = not embeddable.
  // `*` is accepted only when VITE_APP_ENV is not production (ADR-035).
  VITE_FRAME_ANCESTORS: z.optional(z.string()),
});

export interface AppConfig {
  readonly appName: string;
  readonly apiBaseUrl: string;
  readonly appEnv: AppEnv;
  readonly isLocal: boolean;
  readonly isProduction: boolean;
  readonly supportUrl: string | null;
  readonly assetBaseUrl: string | null;
  readonly pollBaseMs: number;
  readonly pollMaxMs: number;
  readonly sessionWarnSeconds: number;
  readonly enableDevTools: boolean;
  readonly frameAncestors: readonly string[];
}

function formatFailure(problems: readonly string[]): string {
  return [
    '',
    'Invalid environment configuration.',
    '',
    ...problems.map((problem) => `  • ${problem}`),
    '',
    'Copy .env.example to .env and fill in the values above.',
    'Every VITE_* value is inlined at build time and ships to the browser — never put a secret in one.',
    '',
  ].join('\n');
}

/**
 * Empty → not embeddable (`frame-ancestors 'none'`). Each token is `*` or an origin
 * (`https://merchant.example`, `http://localhost:5173`). Paths, credentials, and relative
 * values are rejected so a mis-typed allowlist cannot silently fail open.
 */
function parseFrameAncestors(raw: string | undefined, isProduction: boolean): string[] {
  if (raw === undefined || raw.trim() === '') return [];
  const parts = raw.trim().split(/\s+/);
  const origins: string[] = [];
  for (const part of parts) {
    if (part === '*') {
      if (isProduction) {
        throw new Error(
          formatFailure([
            'VITE_FRAME_ANCESTORS: "*" is not allowed when VITE_APP_ENV=production — list explicit merchant origins',
          ]),
        );
      }
      origins.push('*');
      continue;
    }
    try {
      const parsed = new URL(part);
      if (parsed.origin !== part) {
        throw new Error('not an origin');
      }
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
        throw new Error('not an origin');
      }
      origins.push(parsed.origin);
    } catch {
      throw new Error(
        formatFailure([
          `VITE_FRAME_ANCESTORS: "${part}" must be an origin such as https://pay.merchant.example, or "*" outside production`,
        ]),
      );
    }
  }
  return origins;
}

function parseAssetBaseUrl(raw: string | undefined): string | null {
  if (raw === undefined || raw === '') return null;
  try {
    const parsed = new URL(raw);
    if (parsed.origin !== raw) {
      throw new Error('not an origin');
    }
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      throw new Error('not an origin');
    }
    return parsed.origin;
  } catch {
    throw new Error(
      formatFailure([
        'VITE_ASSET_BASE_URL: must be an origin such as https://cdn.example.com, with no path or trailing slash',
      ]),
    );
  }
}

/**
 * Throws with every problem listed at once. One-at-a-time validation turns a five-variable
 * misconfiguration into five build failures.
 */
export function parseEnv(source: unknown): AppConfig {
  const parsed = schema.safeParse(source);

  if (!parsed.success) {
    const problems = parsed.error.issues.map((issue) => {
      const key = issue.path.join('.') || '(root)';
      return `${key}: ${issue.message}`;
    });
    throw new Error(formatFailure(problems));
  }

  const raw = parsed.data;

  if (raw.VITE_POLL_MAX_MS < raw.VITE_POLL_BASE_MS) {
    throw new Error(
      formatFailure([
        `VITE_POLL_MAX_MS: must be >= VITE_POLL_BASE_MS (${String(raw.VITE_POLL_BASE_MS)})`,
      ]),
    );
  }

  const appEnv = raw.VITE_APP_ENV;
  const isProduction = appEnv === 'production';

  return Object.freeze({
    appName: raw.VITE_APP_NAME,
    apiBaseUrl: raw.VITE_API_BASE_URL,
    appEnv,
    isLocal: appEnv === 'local',
    isProduction,
    supportUrl:
      raw.VITE_SUPPORT_URL === undefined || raw.VITE_SUPPORT_URL === ''
        ? null
        : raw.VITE_SUPPORT_URL,
    assetBaseUrl: parseAssetBaseUrl(raw.VITE_ASSET_BASE_URL),
    pollBaseMs: raw.VITE_POLL_BASE_MS,
    pollMaxMs: raw.VITE_POLL_MAX_MS,
    sessionWarnSeconds: raw.VITE_SESSION_WARN_SECONDS,
    // Never on in production, regardless of what the build was handed.
    enableDevTools: isProduction ? false : raw.VITE_ENABLE_DEV_TOOLS,
    frameAncestors: parseFrameAncestors(raw.VITE_FRAME_ANCESTORS, isProduction),
  });
}
